import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  NgModule,
  OnInit,
} from '@angular/core';
import { FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TooltipModule } from '@commons/tooltip/tooltip.component';
import { QuestionBaseDirective } from '@domains/companies/register-form/models/component/question.base.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-form-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10%)', willChange: 'opacity transform' }),
        animate('.2s ease-in-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0)', willChange: 'opacity transform' }),
        animate('.2s ease-in-out', style({ opacity: 0, transform: 'translateY(-10%)' })),
      ]),
    ]),
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
    { provide: NG_VALIDATORS, multi: true, useExisting: forwardRef(() => CheckboxComponent) },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxComponent extends QuestionBaseDirective implements OnInit {
  private static Counter = 0;

  private _value!: boolean;

  private _disabled!: boolean;

  private readonly _id = `wzb-checkbox-${++CheckboxComponent.Counter}`;

  get elm_id(): string {
    return this.id ?? this._id;
  }

  control!: FormControl;

  set value(value: boolean) {
    if (this._value === value) {
      return;
    }
    this._value = value;
    this.control.setValue(value);
    this.onChanged(value);
    this.onTouched();
  }
  get value(): boolean {
    return this._value;
  }

  set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  get displayActions(): boolean {
    return this.question?.displayActions ?? false;
  }

  constructor(readonly element: ElementRef, private readonly cdr: ChangeDetectorRef) {
    super();
  }

  onChanged: (_: boolean) => void = (_: boolean) => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  ngOnInit(): void {
    this.control = this.initFormControl(this.question);

    this.control.valueChanges.pipe(untilDestroyed(this)).subscribe({ next: (value) => (this.value = !!value) });
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChanged = (value) => {
      fn(value);
      this.cdr.markForCheck();
    };
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  writeValue = (value: boolean): boolean => (this.value = !!value);

  onKeydownEnter(event: any): void {
    this.changeValue(event);
  }

  changeValue(event: any): void {
    event.preventDefault();

    if (this.disabled) {
      return;
    }

    this.writeValue(!this.value);
    this.onChanged(this.value);
  }

  trackByName(_: number, ctrl: { label: string; controlName: string }): string {
    return ctrl.controlName;
  }

  next(control: FormControl): void {
    this.control.markAllAsTouched();
    this.control.updateValueAndValidity();
    this.nextQuestion.emit(control);
  }
}

@NgModule({
  imports: [CommonModule, TooltipModule],
  declarations: [CheckboxComponent],
  exports: [CheckboxComponent],
})
export class CheckboxModule {}
